'use strict';

/**
 * Web Notification helper. Title blinking lives on `tabAttention`.
 */
export const notification = {
  /**
   * Show a Web Notification when permitted.
   *
   * `options` accepts all OOTB `Notification` options plus:
   * `onShow`, `onClick`, `onClose`, `onError`.
   *
   * @param {string} title
   * @param {NotificationOptions & {
   *   onShow?: Function,
   *   onClick?: Function,
   *   onClose?: Function,
   *   onError?: Function
   * }} [options]
   * @returns {{
   *   focused: boolean,
   *   permission: string,
   *   notified: boolean
   * }}
   */
  notify(title, options = {}) {
    const { onShow, onClick, onClose, onError, ...notificationOptions } = options;
    const status = {
      permission: this.getPermission(),
      notified: false
    };

    if (status.permission === 'granted') {
      try {
        const webNotification = new Notification(title || '', notificationOptions);

        status.notified = true;

        if (onShow) {
          webNotification.onshow = event => onShow(event, webNotification);
        }

        if (onClick) {
          webNotification.onclick = event => onClick(event, webNotification);
        }

        if (onClose) {
          webNotification.onclose = event => onClose(event, webNotification);
        }

        if (onError) {
          webNotification.onerror = event => onError(event, webNotification);
        }
      } catch (error) {
        status.notified = false;

        if (onError) {
          onError(error);
        }
      }
    }

    return status;
  },

  /**
   * @returns {'granted'|'denied'|'default'|'unsupported'}
   */
  getPermission() {
    if (!('Notification' in window)) {
      return 'unsupported';
    }

    return Notification.permission;
  },

  /**
   * Request notification permission. Must be called from a user gesture.
   *
   * @returns {Promise<'granted'|'denied'|'default'|'unsupported'>}
   */
  requestPermission() {
    if (!('Notification' in window)) {
      return Promise.resolve('unsupported');
    }

    if (Notification.permission !== 'default') {
      return Promise.resolve(Notification.permission);
    }

    return Notification.requestPermission();
  }
};
